import { GENERIC_FUND_TOKENS, MIN_SIGNATURE_TOKEN_LENGTH } from './constants';
import {
  isFundFamilyAcronym,
  isValidMatchPhrase,
  isWordTokenValid,
  normalizePhrase,
  phraseWordCount,
} from './phraseUtils';
import type { ChatLinkTarget } from './types';

function suffixWordsAfterSignature(fundName: string, signature: string): string[] {
  const expanded = normalizePhrase(fundName.replace(/\(([^)]*)\)/g, ' $1 '));
  const parts = expanded.split(/\s+/).filter(Boolean);
  const index = parts.findIndex((part) => part.toLowerCase() === signature.toLowerCase());
  if (index < 0) return [];

  return parts.slice(index + 1).filter((word) => {
    if (!isWordTokenValid(word)) return false;
    if (isFundFamilyAcronym(word)) return false;
    if (GENERIC_FUND_TOKENS.has(word.toLowerCase())) return false;
    return true;
  });
}

/** Distinctive words extracted from fund names (e.g. "Wantirna", "Blackstone"). */
export function extractSignatureTokens(fundName: string): string[] {
  const seen = new Set<string>();
  const tokens: string[] = [];

  for (const part of fundName.split(/[\s/(),\-–]+/)) {
    const token = part.trim();
    if (token.length < MIN_SIGNATURE_TOKEN_LENGTH) continue;
    if (isFundFamilyAcronym(token)) continue;
    if (GENERIC_FUND_TOKENS.has(token.toLowerCase())) continue;
    if (!isValidMatchPhrase(token)) continue;
    const key = token.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tokens.push(token);
  }

  return tokens;
}

/**
 * Adds short match phrases for distinctive tokens in fund names
 * (e.g. "Wantirna" from "FCOF Wantirna"; "Blackstone" from a longer title).
 * Fund-family acronyms alone (FCOF, FCOH) are excluded — too ambiguous.
 */
export function appendSignaturePhrases(targets: ChatLinkTarget[]): void {
  for (const target of targets) {
    if (target.type !== 'fund_investment') continue;

    const existing = new Set(target.matchPhrases.map((phrase) => phrase.toLowerCase()));

    for (const sourcePhrase of [...target.matchPhrases]) {
      for (const token of extractSignatureTokens(sourcePhrase)) {
        const key = token.toLowerCase();
        if (existing.has(key)) continue;
        existing.add(key);
        target.matchPhrases.push(token);
      }
    }

    target.matchPhrases.sort((left, right) => right.length - left.length);
  }
}

/**
 * Adds signature + following whole-word suffixes from the fund title
 * (e.g. "Wantirna A" from "FCOF Wantirna (A Class)").
 */
export function appendSignatureSuffixPhrases(targets: ChatLinkTarget[]): void {
  for (const target of targets) {
    if (target.type !== 'fund_investment') continue;

    const fundLabel = target.displayName;
    const existing = new Set(target.matchPhrases.map((phrase) => phrase.toLowerCase()));

    for (const signature of extractSignatureTokens(fundLabel)) {
      const suffixWords = suffixWordsAfterSignature(fundLabel, signature);
      for (let count = 1; count <= suffixWords.length; count += 1) {
        const suffixPhrase = normalizePhrase(
          `${signature} ${suffixWords.slice(0, count).join(' ')}`,
        );
        if (!isValidMatchPhrase(suffixPhrase)) continue;
        const key = suffixPhrase.toLowerCase();
        if (existing.has(key)) continue;
        existing.add(key);
        target.matchPhrases.push(suffixPhrase);
      }
    }

    target.matchPhrases.sort((left, right) => {
      const wordDiff = phraseWordCount(right) - phraseWordCount(left);
      if (wordDiff !== 0) return wordDiff;
      return right.length - left.length;
    });
  }
}
